import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useNavigate,
  Navigate,
} from "react-router-dom";
import logo from "./assets/images/fortpixel-logo.png";
import "./App.css";
import { Login } from "./components/Login";
import { Dashboard } from "./components/Dashboard";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { onAuthStateChanged } from "firebase/auth";
import { auth, isUserAdmin } from "./firebase";
import { TycoonPage } from "./components/dashboard/TycoonPage";
import { CalculatorPage } from "./components/dashboard/CalculatorPage";
import { CashflowPage } from "./components/dashboard/CashflowPage";
import { VerifiedPurchasesPage } from "./components/dashboard/VerifiedPurchasesPage";
import { MetricsChartPage } from "./components/dashboard/MetricsChartPage";
import { DiscoverHelperPage } from "./components/dashboard/DiscoverHelperPage";
import { TycoonSignsPage } from "./components/dashboard/TycoonSignsPage";
import { SessionLengthPage } from "./components/dashboard/SessionLengthPage";
import { SponsoredRowCalculatorPage } from "./components/dashboard/SponsoredRowCalculatorPage";
import { FortniteApiPage } from "./components/dashboard/FortniteApiPage";
import { FortniteGGPage } from "./components/dashboard/FortniteGGPage";
import NoLiLayout from "./components/noli/NoLiLayout";
import NoLiRedirect from "./components/noli/NoLiRedirect";
const deferNoLi = loader => {
  const Page = React.lazy(loader);
  return function DeferredNoLiPage() {
    return <React.Suspense fallback={<div role="status" className="p-6 text-[#a9b1d6]">Loading page...</div>}><Page /></React.Suspense>;
  };
};
const NutritionTracker = deferNoLi(() => import('./components/noli/NutritionTracker'));
const FitnessTracker = deferNoLi(() => import('./components/noli/FitnessTracker'));
const NotesPage = deferNoLi(() => import('./components/noli/notes/NotesPage'));
const TimelinesPage = deferNoLi(() => import('./components/noli/TimelinesPage'));
const PointsPage = deferNoLi(() => import('./components/noli/PointsPage'));

function HomeRoute({ children }) {
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        navigate("/dashboard/noli/nutrition");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  return children;
}

function NoLiWrapper({ children }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [viewAsUser, setViewAsUser] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const preventNumberInputWheelChange = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const numberInput = target.closest('input[type="number"]');
      if (!(numberInput instanceof HTMLInputElement)) return;

      // Prevent accidental value changes while scrolling over number inputs.
      if (document.activeElement === numberInput) {
        numberInput.blur();
      }
      event.preventDefault();
    };

    document.addEventListener("wheel", preventNumberInputWheelChange, {
      passive: false,
      capture: true,
    });
    return () => {
      document.removeEventListener("wheel", preventNumberInputWheelChange, {
        capture: true,
      });
    };
  }, []);

  useEffect(() => {
    const checkAdmin = async () => {
      if (auth.currentUser) {
        const adminStatus = await isUserAdmin(auth.currentUser.uid);
        setIsAdmin(adminStatus);
      }
    };

    checkAdmin();
  }, [navigate]);

  return (
    <NoLiLayout
      isAdmin={isAdmin}
      onToggleNoLi={() => navigate("/dashboard/tycoon")}
      viewAsUser={viewAsUser}
      setViewAsUser={setViewAsUser}
    >
      {children}
    </NoLiLayout>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        >
          <Route path="tycoon" element={<TycoonPage />} />
          <Route path="calculator" element={<CalculatorPage />} />
          <Route path="cashflow" element={<CashflowPage />} />
          <Route path="verified" element={<VerifiedPurchasesPage />} />
          <Route path="metrics" element={<MetricsChartPage />} />
          <Route path="discover" element={<DiscoverHelperPage />} />
          <Route path="tycoon-signs" element={<TycoonSignsPage />} />
          <Route path="session-length" element={<SessionLengthPage />} />
          <Route
            path="sponsored-row-calculator"
            element={<SponsoredRowCalculatorPage />}
          />
          <Route path="fortnite-api" element={<FortniteApiPage />} />
          <Route path="fortnite-gg" element={<FortniteGGPage />} />
          <Route
            index
            element={<Navigate to="/dashboard/noli/nutrition" replace />}
          />
        </Route>
        <Route
          path="/dashboard/noli"
          element={
            <ProtectedRoute>
              <NoLiWrapper />
            </ProtectedRoute>
          }
        >
          <Route path="nutrition" element={<NutritionTracker />} />
          <Route path="fitness" element={<FitnessTracker />} />
          <Route path="life" element={<NotesPage />} />
          <Route path="timelines" element={<TimelinesPage />} />
          <Route path="points" element={<PointsPage />} />
          <Route
            path="notes"
            element={<NoLiRedirect page="life" />}
          />
          <Route index element={<NoLiRedirect page="nutrition" />} />
        </Route>
        <Route
          path="/dashboard/scavenger/*"
          element={<NoLiRedirect />}
        />
        <Route
          path="/"
          element={
            <HomeRoute>
              <div className="min-h-screen flex flex-col items-center justify-between bg-black text-white">
                <div className="flex-grow flex flex-col items-center justify-center">
                  <div className="mb-8">
                    <img
                      src={logo}
                      alt="FortPixel Studios Logo"
                      className="mx-auto"
                    />
                  </div>
                  <h1 className="text-4xl font-bold mb-4 text-fortpixel-gold">
                    Coming Soon
                  </h1>
                  <Link
                    to="/login"
                    className="mt-4 px-6 py-2 bg-fortpixel-gold hover:bg-yellow-600 text-black font-medium rounded-md"
                  >
                    Admin Login
                  </Link>
                </div>
                <footer className="w-full text-center py-4">
                  <p className="text-sm text-gray-400">
                    © {new Date().getFullYear()} FortPixel Studios. All rights
                    reserved.
                  </p>
                </footer>
              </div>
            </HomeRoute>
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
